import styled from 'styled-components';

import { IconButton } from '@mui/material';

import { MenuItem } from 'containers/Dashboard/components/SideBar/components/FolderTreeItem/styles';

import {
  ExpandIcon,
  FolderItem,
  FolderItemContent,
  FolderName,
} from 'containers/Documents/components/FolderTreePicker/styles';

export const Card = styled.div`
  background: ${({ theme }) => theme.palette.common.white};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 8px;

  & + & {
    margin-top: 16px;
  }
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h4.fontSize};
  font-weight: 500;
  line-height: 150%;
  color: ${({ theme }) => theme.palette.text.primary};
  overflow-wrap: anywhere;
`;

export const CardHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

export const CardBody = styled.div`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(2.5)};
`;

export const SectionDescription = styled.p`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  line-height: 150%;
  margin: 0 0 16px 0;
  max-width: 640px;
`;

export const RetryRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

/** Tree and detail panel side by side; below lg the panel stacks above the tree. */
export const SplitLayout = styled.div<{ $panelOpen: boolean }>`
  display: grid;
  grid-template-columns: ${({ $panelOpen }) => ($panelOpen ? 'minmax(0, 2fr) minmax(320px, 1fr)' : 'minmax(0, 1fr)')};
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: start;

  && > * {
    margin-top: 0;
  }

  ${({ theme }) => theme.breakpoints.down('lg')} {
    grid-template-columns: minmax(0, 1fr);

    & > aside {
      order: -1;
    }
  }
`;

export const PanelPath = styled.div`
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  line-height: 150%;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const PanelCountGroup = styled.section`
  & + & {
    margin-top: 12px;
  }
`;

export const PanelCountHeading = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(0.5)};
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: 600;
  line-height: 150%;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const PanelCounts = styled.dl`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${({ theme }) => theme.spacing(0.5)} ${({ theme }) => theme.spacing(2)};
  margin: 0;
`;

export const PanelCountsEnd = styled.div`
  margin: 12px 0 16px;

  &:first-child {
    margin-top: 0;
  }
`;

export const PanelLabel = styled.dt`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  line-height: 150%;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const PanelCount = styled.dd`
  margin: 0;
  text-align: right;
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  line-height: 150%;
  color: ${({ theme }) => theme.palette.text.primary};
  font-variant-numeric: tabular-nums;
`;

export const UnitRowShell = styled.div<{
  $depth: number;
  $selected: boolean;
  $selectable?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing(0.5)} ${({ theme }) => theme.spacing(1.5)};
  min-height: 44px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  cursor: ${({ $selectable }) => ($selectable ? 'pointer' : 'default')};
  background: ${({ $selected, theme }) => ($selected ? theme.tint.primary.light : 'transparent')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $selected, theme }) => ($selected ? theme.tint.primary.medium : theme.palette.action.hover)};
  }
`;

export const NameCell = styled.div<{ $depth: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding-left: ${({ $depth }) => $depth * 20}px;
`;

export const UnitName = styled.span<{ $selected: boolean }>`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  line-height: 150%;
  overflow-wrap: anywhere;
`;

const chevronRotation = (expanded: boolean): string => (expanded ? '0deg' : '-90deg');

export const ExpandToggle = styled.button<{ $visible: boolean; $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  color: ${({ theme }) => theme.palette.text.secondary};

  svg {
    transform: rotate(${({ $expanded }) => chevronRotation($expanded)});
    transition: transform 0.2s ease;
  }
`;

/** The picker's chevron, turned the same way as the page tree's. */
export const PickerExpandIcon = styled(ExpandIcon)`
  transform: rotate(${({ $expanded }) => chevronRotation($expanded)});
`;

export const RowCount = styled.span`
  width: 96px;
  flex-shrink: 0;
  text-align: right;
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  line-height: 150%;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-variant-numeric: tabular-nums;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 88px;
  }
`;

export const TreeHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${({ theme }) => theme.palette.common.white};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  line-height: 150%;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const TreeHeaderUnit = styled.span`
  flex: 1;
  min-width: 0;
`;

export const TreeHeaderCount = styled.span`
  width: 96px;
  flex-shrink: 0;
  text-align: right;
  white-space: nowrap;
  cursor: help;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 88px;
  }
`;

export const SkeletonRow = styled.div<{ $depth?: number }>`
  height: 20px;
  margin: 12px 0 12px ${({ $depth }) => ($depth ?? 0) * 20}px;
  border-radius: 4px;
  background: ${({ theme }) => theme.palette.grey[200]};
`;

/** Sized from the window, never from the selection, so the panel beside it stays in view; stacked below lg, the page scrolls. */
export const TreeScroll = styled.div`
  max-height: calc(100vh - 280px);
  overflow-y: auto;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    max-height: none;
  }
`;

export const TreeGuidance = styled.div`
  padding: 16px 12px 4px;
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  line-height: 150%;
`;

export const PickerRow = styled(FolderItem)<{ $disabled: boolean }>`
  ${({ $disabled }) => ($disabled ? 'opacity: 0.4; cursor: not-allowed;' : '')}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: -2px;
  }
`;

export const ROW_MENU_WIDTH = 28;
export const RowMenuButton = styled(IconButton)`
  flex-shrink: 0;
  width: ${ROW_MENU_WIDTH}px;
  height: ${ROW_MENU_WIDTH}px;
  padding: 4px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const DangerMenuItem = styled(MenuItem)`
  color: ${({ theme }) => theme.palette.error.main};
`;

export const TreeHeaderMenuSlot = styled.span`
  flex-shrink: 0;
  width: ${ROW_MENU_WIDTH}px;
`;

/** Units have no folder icon, so the name drops the folder row's icon margin. */
export const PickerItemContent = styled(FolderItemContent)`
  padding: 4px 8px;
`;

export const PickerName = styled(FolderName)`
  margin-left: 0;
  color: ${({ theme }) => theme.palette.text.primary};
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
`;

export const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

export const RequiredMark = styled.span`
  color: ${({ theme }) => theme.palette.error.main};
`;

export const DialogGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

/** The open unit picker, as wide as the field it opened from. */
export const PickerPopoverBody = styled.div<{ $width?: number }>`
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  max-width: 100%;
  padding: ${({ theme }) => theme.spacing(1.5)};
`;
